import type {
  BuildingLinearThermalCoeffs,
  ComfortInput,
  ComfortResult,
  HourlyIdealLoadResult,
  HourlyThermalMeta,
  HourlyThermalZoneBundle,
  OpenMeteoClimatePayload,
} from '@comforttool/comfortcore';

const raw = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/$/, '');

export function apiBase(): string {
  return raw || '/api';
}

/** Uniforme API-fout (Worker 404+); oudere endpoints blijven `ok: false` / `errors[]` gebruiken. */
export type ComfortApiErrorBody = {
  code: string;
  message: string;
  details?: unknown;
};

export function extractComfortApiMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const o = data as Record<string, unknown>;
  if (typeof o.message === 'string' && o.message.trim()) return o.message;
  if (Array.isArray(o.errors) && o.errors.length && typeof o.errors[0] === 'string') {
    return o.errors.join('; ');
  }
  if (typeof o.error === 'string' && o.error.trim()) return o.error;
  if (o.error && typeof o.error === 'object') {
    const e = o.error as Record<string, unknown>;
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
    if (typeof e.code === 'string') return e.code;
  }
  return fallback;
}

export interface ComforttoolConfig {
  ok?: boolean;
  checkout_available?: boolean;
  report_available?: boolean;
  accounts_available?: boolean;
  stripe_configured?: boolean;
  stripe_webhook_configured?: boolean;
  password_reset_mail_configured?: boolean;
  support_desk_available?: boolean;
  public_base_url_configured?: boolean;
  products?: {
    woon?: { available?: boolean };
    checkout_addons?: { available?: boolean };
  };
}

export async function fetchComforttoolConfig(): Promise<ComforttoolConfig> {
  try {
    const r = await fetch(`${apiBase()}/comforttool/config`, { credentials: 'omit' });
    const text = await r.text();
    const t = text.trim();
    if (!t.startsWith('{')) {
      console.warn(
        '[ComfortTool] API-config is geen JSON (controleer VITE_API_BASE_URL / Worker).',
        t.slice(0, 80)
      );
      return { ok: false, support_desk_available: false };
    }
    return JSON.parse(t) as ComforttoolConfig;
  } catch (e) {
    console.warn('[ComfortTool] config ophalen mislukt', e);
    return { ok: false, support_desk_available: false };
  }
}

/** Open‑Meteo archief (Worker cache) — graaddagen voor postcode (4 cijfers). */
export async function fetchClimateForPostcode(
  postcode: string
): Promise<{ ok: true; climate: OpenMeteoClimatePayload } | { ok: false; error: string }> {
  const r = await fetch(
    `${apiBase()}/comforttool/climate?postcode=${encodeURIComponent(postcode.trim())}`
  );
  const j = (await r.json().catch(() => ({}))) as {
    ok?: boolean;
    climate?: OpenMeteoClimatePayload;
    error?: string;
  };
  if (!r.ok || j.ok === false) {
    return { ok: false, error: j.error ?? 'climate_failed' };
  }
  if (j.climate) return { ok: true, climate: j.climate };
  return { ok: false, error: 'invalid_response' };
}

/** Zelfde uitkomst als lokaal `computeComfortEngine` — parity-checks / server-only modus. */
export async function calculateComfortOnServer(
  input: ComfortInput
): Promise<
  | { ok: true; result: ComfortResult; climate: OpenMeteoClimatePayload | null; disclaimer?: string }
  | { ok: false; errors: string[]; message?: string }
> {
  const r = await fetch(`${apiBase()}/comforttool/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ input }),
  });
  const j = (await r.json().catch(() => ({}))) as {
    ok?: boolean;
    result?: ComfortResult;
    climate?: OpenMeteoClimatePayload | null;
    errors?: string[];
    message?: string;
    disclaimer?: string;
  };
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      errors: j.errors ?? [extractComfortApiMessage(j, 'request_failed')],
      message: j.message,
    };
  }
  if (j.result) {
    return { ok: true, result: j.result, climate: j.climate ?? null, disclaimer: j.disclaimer };
  }
  return { ok: false, errors: ['invalid_response'] };
}

export type HourlyThermalApiSuccess = {
  ok: true;
  hourlyThermal: HourlyIdealLoadResult;
  hourlyThermalRc1: HourlyIdealLoadResult;
  thermalMeta: HourlyThermalMeta;
  coeffs: BuildingLinearThermalCoeffs;
  hourlyThermalZones?: HourlyThermalZoneBundle[];
  solarGainsTotal_W?: number[];
  uncoupledMultiZone?: boolean;
  openMeteoHourly: {
    source: string;
    attribution: string;
    startDate: string;
    endDate: string;
    locationLabel: string;
    hours: number;
    note: string;
  };
};

export type ThermalInertiaOption = 'licht' | 'normaal' | 'zwaar';

/** Uurlijks weer + quasi-stationair én 1R1C (thermische massa) op de Worker. */
export async function fetchComforttoolHourlyThermal(
  input: ComfortInput,
  options?: {
    koelgrensc?: number;
    thermalInertia?: ThermalInertiaOption;
    /** Zelfde gedrag als vóór multi-zone: één geaggregeerde UA. */
    forceSingleZoneHourly?: boolean;
  }
): Promise<HourlyThermalApiSuccess | { ok: false; errors: string[]; error?: string }> {
  const r = await fetch(`${apiBase()}/comforttool/hourly-thermal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      input,
      koelgrensc: options?.koelgrensc,
      thermalInertia: options?.thermalInertia,
      forceSingleZoneHourly: options?.forceSingleZoneHourly,
    }),
  });
  const j = (await r.json().catch(() => ({}))) as {
    ok?: boolean;
    hourlyThermal?: HourlyIdealLoadResult;
    hourlyThermalRc1?: HourlyIdealLoadResult;
    thermalMeta?: HourlyThermalMeta;
    coeffs?: BuildingLinearThermalCoeffs;
    hourlyThermalZones?: HourlyThermalZoneBundle[];
    solarGainsTotal_W?: number[];
    uncoupledMultiZone?: boolean;
    openMeteoHourly?: HourlyThermalApiSuccess['openMeteoHourly'];
    errors?: string[];
    error?: string;
    disclaimer?: string;
  };
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      errors: j.errors ?? [j.error ?? extractComfortApiMessage(j, 'hourly_thermal_failed')],
      error: j.error,
    };
  }
  if (j.hourlyThermal && j.hourlyThermalRc1 && j.thermalMeta && j.coeffs && j.openMeteoHourly) {
    return {
      ok: true,
      hourlyThermal: j.hourlyThermal,
      hourlyThermalRc1: j.hourlyThermalRc1,
      thermalMeta: j.thermalMeta,
      coeffs: j.coeffs,
      hourlyThermalZones: j.hourlyThermalZones,
      solarGainsTotal_W: j.solarGainsTotal_W,
      uncoupledMultiZone: j.uncoupledMultiZone,
      openMeteoHourly: j.openMeteoHourly,
    };
  }
  return { ok: false, errors: ['invalid_response'] };
}

/** Rapport (Markdown): Comfort Basis = vaste samenvatting zonder OpenAI; Plus = AI (vereist OPENAI_API_KEY). */
export async function fetchComfortReport(
  input: ComfortInput,
  options?: { bundleTier?: 'basis' | 'plus'; brandingLine?: string }
): Promise<
  | { ok: true; markdown: string; disclaimer?: string }
  | { ok: false; error: string; message?: string }
> {
  const bundle_tier = options?.bundleTier === 'basis' ? 'basis' : 'plus';
  const branding_line = options?.brandingLine?.trim().slice(0, 200) ?? '';
  const r = await fetch(`${apiBase()}/comforttool/report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ input, bundle_tier, branding_line }),
  });
  const j = (await r.json().catch(() => ({}))) as {
    ok?: boolean;
    markdown?: string;
    error?: string;
    message?: string;
    errors?: string[];
    disclaimer?: string;
  };
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      error: j.error ?? 'report_failed',
      message: j.message ?? j.errors?.join('; ') ?? extractComfortApiMessage(j, 'report_failed'),
    };
  }
  if (j.markdown) return { ok: true, markdown: j.markdown, disclaimer: j.disclaimer };
  return { ok: false, error: 'invalid_response' };
}

function withBearer(token: string | null | undefined, init: RequestInit = {}): RequestInit {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && init.body !== null) {
    headers.set('Content-Type', 'application/json');
  }
  if (token?.trim()) headers.set('Authorization', `Bearer ${token.trim()}`);
  return { ...init, headers };
}

export async function startComforttoolCheckout(
  body: {
    customer_email: string;
    input: ComfortInput;
    bundle_tier?: 'basis' | 'plus';
    addons?: string[];
    success_url: string;
    cancel_url: string;
  },
  authToken?: string | null
): Promise<{ url?: string; session_id?: string; error?: string; message?: string }> {
  const r = await fetch(`${apiBase()}/comforttool/checkout`, {
    method: 'POST',
    ...withBearer(authToken, { body: JSON.stringify(body) }),
  });
  const j = (await r.json().catch(() => ({}))) as {
    url?: string;
    session_id?: string;
    error?: string;
    message?: string;
  };
  if (!r.ok) return { error: j.error ?? 'request_failed', message: j.message };
  return j;
}

export async function verifyCheckoutSession(sessionId: string): Promise<{
  paid?: boolean;
  product?: string | null;
  tier?: string | null;
  bundle_tier?: string | null;
  pending_checkout_id?: string | null;
  customer_email?: string | null;
  error?: string;
}> {
  const r = await fetch(`${apiBase()}/comforttool/verify-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
  });
  return r.json() as Promise<{
    paid?: boolean;
    product?: string | null;
    tier?: string | null;
    bundle_tier?: string | null;
    pending_checkout_id?: string | null;
    customer_email?: string | null;
    error?: string;
  }>;
}

export type AuthUser = { id: number; email: string; is_superadmin: boolean };

export async function authRegister(
  email: string,
  password: string,
  signupSource?: string | null
): Promise<
  | { ok: true; token: string; user: AuthUser; expires_at: number }
  | { ok: false; error: string; message?: string }
> {
  const body: { email: string; password: string; signup_source?: string } = { email, password };
  const s = signupSource?.trim();
  if (s) body.signup_source = s.slice(0, 80);
  const r = await fetch(`${apiBase()}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      error: String(j.error ?? 'register_failed'),
      message: typeof j.message === 'string' ? j.message : undefined,
    };
  }
  if (typeof j.token === 'string' && j.user && typeof j.user === 'object') {
    const u = j.user as AuthUser;
    return {
      ok: true,
      token: j.token,
      user: u,
      expires_at: Number(j.expires_at) || 0,
    };
  }
  return { ok: false, error: 'invalid_response' };
}

export async function authForgotPassword(
  email: string
): Promise<{ ok: true; message?: string } | { ok: false; error: string; message?: string }> {
  const r = await fetch(`${apiBase()}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      error: String(j.error ?? 'request_failed'),
      message: typeof j.message === 'string' ? j.message : undefined,
    };
  }
  return { ok: true, message: typeof j.message === 'string' ? j.message : undefined };
}

export async function authResetPassword(
  token: string,
  password: string
): Promise<{ ok: true; message?: string } | { ok: false; error: string; message?: string }> {
  const r = await fetch(`${apiBase()}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      error: String(j.error ?? 'request_failed'),
      message: typeof j.message === 'string' ? j.message : undefined,
    };
  }
  return { ok: true, message: typeof j.message === 'string' ? j.message : undefined };
}

export async function authLogin(
  email: string,
  password: string
): Promise<
  | { ok: true; token: string; user: AuthUser; expires_at: number }
  | { ok: false; error: string; message?: string }
> {
  const r = await fetch(`${apiBase()}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      error: String(j.error ?? 'login_failed'),
      message: typeof j.message === 'string' ? j.message : undefined,
    };
  }
  if (typeof j.token === 'string' && j.user && typeof j.user === 'object') {
    const u = j.user as AuthUser;
    return {
      ok: true,
      token: j.token,
      user: u,
      expires_at: Number(j.expires_at) || 0,
    };
  }
  return { ok: false, error: 'invalid_response' };
}

export async function authLogout(token: string): Promise<boolean> {
  const r = await fetch(`${apiBase()}/auth/logout`, {
    method: 'POST',
    ...withBearer(token, {}),
  });
  const j = (await r.json().catch(() => ({}))) as { ok?: boolean };
  return r.ok && j.ok === true;
}

export async function authMe(
  token: string
): Promise<{ ok: true; user: AuthUser } | { ok: false }> {
  const r = await fetch(`${apiBase()}/auth/me`, { ...withBearer(token, { method: 'GET' }) });
  const j = (await r.json().catch(() => ({}))) as { ok?: boolean; user?: AuthUser };
  if (!r.ok || j.ok === false || !j.user) return { ok: false };
  return { ok: true, user: j.user };
}

export async function claimPurchase(
  token: string,
  sessionId: string
): Promise<
  | { ok: true; purchase_id: number; already_claimed?: boolean }
  | { ok: false; error: string; message?: string }
> {
  const r = await fetch(`${apiBase()}/comforttool/purchases/claim`, {
    method: 'POST',
    ...withBearer(token, { body: JSON.stringify({ session_id: sessionId }) }),
  });
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      error: String(j.error ?? 'claim_failed'),
      message: typeof j.message === 'string' ? j.message : undefined,
    };
  }
  const id = Number(j.purchase_id);
  if (!Number.isFinite(id)) return { ok: false, error: 'invalid_response' };
  return { ok: true, purchase_id: id, already_claimed: j.already_claimed === true };
}

/** CSV-samenvatting van de opgeslagen invoer (authenticeerd, eigenaar van de aankoop). */
export async function downloadPurchaseCsv(
  token: string,
  purchaseId: number
): Promise<{ ok: true; blob: Blob; filename: string } | { ok: false; message: string }> {
  const r = await fetch(`${apiBase()}/comforttool/purchases/${purchaseId}/csv`, {
    ...withBearer(token, { method: 'GET' }),
  });
  if (!r.ok) {
    const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
    return { ok: false, message: extractComfortApiMessage(j, 'Download mislukt.') };
  }
  let filename = `comforttool-aankoop-${purchaseId}.csv`;
  const cd = r.headers.get('Content-Disposition');
  const m = cd?.match(/filename="([^"]+)"/);
  if (m?.[1]) filename = m[1];
  const blob = await r.blob();
  return { ok: true, blob, filename };
}

export type PurchaseListItem = {
  id: number;
  stripe_checkout_session_id: string;
  bundle_tier: string;
  engine_version: string;
  created_at: number;
  report_regenerations_used: number;
  has_report: number;
  /** Stripe billing lifecycle: active | partially_refunded | refunded | dispute_open | dispute_lost */
  billing_status?: string;
};

export async function listPurchases(
  token: string
): Promise<{ ok: true; purchases: PurchaseListItem[] } | { ok: false }> {
  const r = await fetch(`${apiBase()}/comforttool/purchases`, { ...withBearer(token, { method: 'GET' }) });
  const j = (await r.json().catch(() => ({}))) as {
    ok?: boolean;
    purchases?: PurchaseListItem[];
  };
  if (!r.ok || j.ok === false || !Array.isArray(j.purchases)) return { ok: false };
  return { ok: true, purchases: j.purchases };
}

export type PurchaseDetail = {
  id: number;
  stripe_checkout_session_id: string;
  bundle_tier: string;
  addons_json: string;
  comfort_input_json: string;
  engine_version: string;
  report_regenerations_used: number;
  last_report_markdown: string | null;
  last_report_disclaimer: string | null;
  last_report_at: number | null;
  created_at: number;
  max_regenerations: number;
  billing_status?: string;
};

export async function getPurchase(
  token: string,
  id: number
): Promise<{ ok: true; purchase: PurchaseDetail } | { ok: false }> {
  const r = await fetch(`${apiBase()}/comforttool/purchases/${id}`, {
    ...withBearer(token, { method: 'GET' }),
  });
  const j = (await r.json().catch(() => ({}))) as { ok?: boolean; purchase?: PurchaseDetail };
  if (!r.ok || j.ok === false || !j.purchase) return { ok: false };
  return { ok: true, purchase: j.purchase };
}

export async function regeneratePurchaseReport(
  token: string,
  id: number,
  brandingLine?: string
): Promise<
  | { ok: true; markdown: string; disclaimer?: string; report_regenerations_used: number }
  | { ok: false; error: string; message?: string }
> {
  const r = await fetch(`${apiBase()}/comforttool/purchases/${id}/report`, {
    method: 'POST',
    ...withBearer(token, {
      body: JSON.stringify({ branding_line: brandingLine?.trim().slice(0, 200) ?? '' }),
    }),
  });
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      error: String(j.error ?? 'report_failed'),
      message: typeof j.message === 'string' ? j.message : undefined,
    };
  }
  if (typeof j.markdown === 'string') {
    return {
      ok: true,
      markdown: j.markdown,
      disclaimer: typeof j.disclaimer === 'string' ? j.disclaimer : undefined,
      report_regenerations_used: Number(j.report_regenerations_used) || 0,
    };
  }
  return { ok: false, error: 'invalid_response' };
}

export async function adminStats(
  token: string
): Promise<{ ok: true; stats: { users: number; purchases: number; pending_checkouts: number } } | { ok: false }> {
  const r = await fetch(`${apiBase()}/admin/stats`, { ...withBearer(token, { method: 'GET' }) });
  const j = (await r.json().catch(() => ({}))) as {
    ok?: boolean;
    stats?: { users: number; purchases: number; pending_checkouts: number };
  };
  if (!r.ok || j.ok === false || !j.stats) return { ok: false };
  return { ok: true, stats: j.stats };
}

export async function adminPurchasesList(
  token: string,
  limit = 50,
  offset = 0
): Promise<
  | {
      ok: true;
      purchases: Array<{
        id: number;
        user_id: number;
        user_email: string;
        stripe_checkout_session_id: string;
        bundle_tier: string;
        created_at: number;
        report_regenerations_used: number;
        billing_status?: string;
      }>;
    }
  | { ok: false }
> {
  const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const r = await fetch(`${apiBase()}/admin/purchases?${q}`, { ...withBearer(token, { method: 'GET' }) });
  const j = (await r.json().catch(() => ({}))) as { ok?: boolean; purchases?: unknown[] };
  if (!r.ok || j.ok === false || !Array.isArray(j.purchases)) return { ok: false };
  return { ok: true, purchases: j.purchases as never };
}

export async function adminUsersList(
  token: string,
  limit = 50,
  offset = 0
): Promise<
  | { ok: true; users: Array<{ id: number; email: string; is_superadmin: number; created_at: number }> }
  | { ok: false }
> {
  const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const r = await fetch(`${apiBase()}/admin/users?${q}`, { ...withBearer(token, { method: 'GET' }) });
  const j = (await r.json().catch(() => ({}))) as { ok?: boolean; users?: unknown[] };
  if (!r.ok || j.ok === false || !Array.isArray(j.users)) return { ok: false };
  return { ok: true, users: j.users as never };
}

export async function adminResetPurchaseReportRegenerations(
  token: string,
  purchaseId: number
): Promise<{ ok: true; purchase_id: number } | { ok: false; message?: string }> {
  const r = await fetch(`${apiBase()}/admin/purchases/${purchaseId}/reset-report-regenerations`, {
    method: 'POST',
    ...withBearer(token, {}),
  });
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      message: extractComfortApiMessage(j, 'Actie mislukt.'),
    };
  }
  return { ok: true, purchase_id: Number(j.purchase_id) || purchaseId };
}

export type SupportDeskMessage = { role: 'user' | 'assistant'; content: string };

const SUPPORT_DESK_TIMEOUT_MS = 72_000;

export async function postSupportDeskChat(
  body: {
    messages: SupportDeskMessage[];
    page?: string;
  },
  authToken?: string | null
): Promise<{ ok: true; message: string } | { ok: false; error: string; message?: string }> {
  const ac = new AbortController();
  const timeoutId = setTimeout(() => ac.abort(), SUPPORT_DESK_TIMEOUT_MS);
  let r: Response;
  try {
    r = await fetch(`${apiBase()}/comforttool/support-desk`, {
      ...withBearer(authToken, {
        method: 'POST',
        body: JSON.stringify(body),
        signal: ac.signal,
      }),
    });
  } catch (e) {
    clearTimeout(timeoutId);
    const aborted = e instanceof DOMException && e.name === 'AbortError';
    return {
      ok: false,
      error: aborted ? 'timeout' : 'network_error',
      message: aborted
        ? 'Het duurde te lang — de verbinding werd afgebroken. Probeer het zo nog eens, of gebruik contact als het blijft gebeuren.'
        : 'Geen verbinding met de server. Controleer je internet en probeer opnieuw.',
    };
  }
  clearTimeout(timeoutId);
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok || j.ok === false) {
    return {
      ok: false,
      error: String(j.error ?? 'request_failed'),
      message: typeof j.message === 'string' ? j.message : extractComfortApiMessage(j, 'Installatieadviseur tijdelijk niet beschikbaar.'),
    };
  }
  if (typeof j.message === 'string' && j.message.trim()) {
    return { ok: true, message: j.message.trim() };
  }
  return { ok: false, error: 'invalid_response', message: 'Leeg antwoord.' };
}
